import { Badge } from '@/components/ui/badge';
import { Shield, ShieldAlert, ShieldCheck, PhoneOff, MessageSquareOff, RefreshCw, Skull } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ContactStatusBadgesProps {
  contact: {
    contact_type?: string;
    customer_status?: string;
    satisfaction_level?: string;
    verification_status?: string;
    do_not_call?: boolean;
    do_not_message?: boolean;
    contact_lifecycle_status?: string;
    reactivation_status?: string;
    reactivation_cycle_count?: number;
  };
}

export function ContactStatusBadges({ contact }: ContactStatusBadgesProps) {
  const { t } = useTranslation();

  const getContactTypeStyle = (type?: string) => {
    switch (type) {
      case 'prospect': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'active_customer': return 'bg-green-100 text-green-800 border-green-200';
      case 'past_customer': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getSatisfactionStyle = (level?: string) => {
    switch (level) {
      case 'promoter': return 'bg-green-100 text-green-800 border-green-200';
      case 'neutral': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'detractor': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getVerificationIcon = (status?: string) => {
    switch (status) {
      case 'verified': return <ShieldCheck className="h-3 w-3" />;
      case 'invalid': return <ShieldAlert className="h-3 w-3" />;
      default: return <Shield className="h-3 w-3" />;
    }
  };

  const getVerificationStyle = (status?: string) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800 border-green-200';
      case 'invalid': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getContactTypeLabel = (type?: string) => {
    switch (type) {
      case 'prospect': return t('contactTypes.prospect');
      case 'active_customer': return t('contactTypes.activeCustomer');
      case 'past_customer': return t('contactTypes.pastCustomer');
      default: return type;
    }
  };

  const getCustomerStatusLabel = (status?: string) => {
    switch (status) {
      case 'in_process': return t('customerStatus.inProcess');
      case 'completed': return t('customerStatus.completed');
      case 'churned_client_decision': return t('customerStatus.churnedClientDecision');
      case 'churned_company_decision': return t('customerStatus.churnedCompanyDecision');
      default: return status;
    }
  };

  const getSatisfactionLabel = (level?: string) => {
    switch (level) {
      case 'promoter': return t('satisfactionLevel.promoter');
      case 'neutral': return t('satisfactionLevel.neutral');
      case 'detractor': return t('satisfactionLevel.detractor');
      default: return level;
    }
  };

  const getVerificationLabel = (status?: string) => {
    switch (status) {
      case 'verified': return t('verificationStatus.verified');
      case 'unverified': return t('verificationStatus.unverified');
      case 'invalid': return t('verificationStatus.invalid');
      default: return status;
    }
  };

  const getLifecycleLabel = (status?: string) => {
    switch (status) {
      case 'new': return t('lifecycleStatus.new');
      case 'engaged': return t('lifecycleStatus.engaged');
      case 'qualified': return t('lifecycleStatus.qualified');
      case 'dead': return t('lifecycleStatus.dead');
      default: return status;
    }
  };

  const isInReactivation = contact.reactivation_status === 'scheduled' || contact.reactivation_status === 'in_progress';
  const isDead = contact.contact_lifecycle_status === 'dead';

  return (
    <div className="flex flex-wrap gap-2">
      {contact.contact_type && (
        <Badge variant="outline" className={getContactTypeStyle(contact.contact_type)}>
          {getContactTypeLabel(contact.contact_type)}
        </Badge>
      )}

      {contact.customer_status && (
        <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">
          {getCustomerStatusLabel(contact.customer_status)}
        </Badge>
      )}

      {contact.satisfaction_level && (
        <Badge variant="outline" className={getSatisfactionStyle(contact.satisfaction_level)}>
          {getSatisfactionLabel(contact.satisfaction_level)}
        </Badge>
      )}

      {contact.verification_status && (
        <Badge variant="outline" className={`flex items-center gap-1 ${getVerificationStyle(contact.verification_status)}`}>
          {getVerificationIcon(contact.verification_status)}
          {getVerificationLabel(contact.verification_status)}
        </Badge>
      )}

      {contact.contact_lifecycle_status && contact.contact_lifecycle_status !== 'new' && !isDead && (
        <Badge variant="outline" className="bg-indigo-100 text-indigo-800 border-indigo-200">
          {getLifecycleLabel(contact.contact_lifecycle_status)}
        </Badge>
      )}

      {/* Reactivation Badge */}
      {isInReactivation && (
        <Badge variant="outline" className="flex items-center gap-1 bg-orange-100 text-orange-800 border-orange-200">
          <RefreshCw className="h-3 w-3" />
          {t('contacts.reactivationCycle', { current: contact.reactivation_cycle_count || 1, total: 6 })}
        </Badge>
      )}

      {/* Dead Badge */}
      {isDead && (
        <Badge variant="destructive" className="flex items-center gap-1">
          <Skull className="h-3 w-3" />
          {t('contacts.dead')}
        </Badge>
      )}

      {contact.do_not_call && (
        <Badge variant="destructive" className="flex items-center gap-1">
          <PhoneOff className="h-3 w-3" />
          {t('contacts.doNotCall')}
        </Badge>
      )}

      {contact.do_not_message && (
        <Badge variant="destructive" className="flex items-center gap-1">
          <MessageSquareOff className="h-3 w-3" />
          {t('contacts.doNotMessage')}
        </Badge>
      )}
    </div>
  );
}
